import { toast } from "sonner";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";

import { ControlEditor } from "../ControlEditor";
import { useActiveCanvas, usePrevious } from "../../utils/utils";
import { getUnexpiredToken, useCanvasId } from "../../utils/utils";
import { useCodebaseStore } from "../../store/zustand/codebaseStore";
import { TempoElement } from "../../utils/tempoElement";
import { track } from "@/utils/events/tracker";
import { useSelectedNavTreeNode } from "@/components/Canvas/stores/canvasStore";

type StoryControlType = "text" | "number" | "boolean" | "select" | "object";

// Helper to generate placeholder text for story controls based on their type/options
export const generateStoryControlPlaceholder = (control: {
  type?: string;
  options?: (string | number | boolean)[];
}): string => {
  // For select controls with options, show the options
  if (
    control.type === "select" &&
    control.options &&
    control.options.length > 0
  ) {
    const optionStrings = control.options.map((opt) => {
      if (typeof opt === "string") {
        // Quote string options
        return `"${opt}"`;
      }
      return String(opt);
    });
    return optionStrings.join(" | ");
  }

  // For basic types, just show the type name
  if (control.type === "number") {
    return "number";
  }

  if (control.type === "text") {
    return "string";
  }

  if (control.type === "boolean") {
    return "boolean";
  }

  return "";
};

interface StoryControlBase {
  type?: StoryControlType;
  disable?: boolean;
  description?: string;
  placeholder?: string;
}

export type StoryControl =
  | (StoryControlBase & {
      type: "select";
      options?: (string | number | boolean)[];
      labels?: Record<string, string>;
    })
  | (StoryControlBase & {
      type: "number";
      max?: number;
      min?: number;
      step?: number;
    })
  | (StoryControlBase & { type: "text" })
  | (StoryControlBase & { type: "boolean" })
  | (StoryControlBase & { type: "object" });

export type StoryControlsSpec = Record<string, StoryControl>;
export type StoryControlValue =
  | string
  | number
  | boolean
  | Record<string, any>
  | Array<any>;
export type StoryControlValues = Record<string, StoryControlValue>;

const useStoryboardControls = () => {
  const { selectedElement } = useSelectedNavTreeNode();
  const storyboardId = selectedElement?.storyboardId;
  const activeCanvas = useActiveCanvas();
  const storyboard = activeCanvas?.storyboards?.find(
    (sb) => sb.id === storyboardId,
  );

  const canvasId = useCanvasId();
  const { session } = useSession();
  const storyArgValues = useCodebaseStore((s) => s.storyArgValues);

  const storyUniqueIdentifier = storyboard?.storyUniqueIdentifier;
  const cachedControls = useCodebaseStore((s) =>
    storyUniqueIdentifier ? s.cachedControlsSpecs[storyUniqueIdentifier] : null,
  );
  const setCachedControls = useCodebaseStore((s) => s.setCachedControls);

  // Store controls and values as records keyed by storyCodebaseId
  const [controlsRecord, setControlsRecord] = useState<
    Record<string, StoryControlsSpec>
  >({});
  const [valuesRecord, setValuesRecord] = useState<
    Record<string, StoryControlValues>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current storyCodebaseId-specific values, or empty if no storyCodebaseId
  // TODO: We should move everything to be read from storyboardId, where both
  // the values and the control types are both sent from the iframe itself.
  // Currently there's a separate parsing endpoint that is necessary for props
  // but not for controls since controls can be read from the exported meta
  // property.
  const controls = storyUniqueIdentifier
    ? controlsRecord[storyUniqueIdentifier] || cachedControls || null
    : null;
  const values = storyboardId
    ? valuesRecord[storyboardId] || (storyArgValues[storyboardId] ?? {})
    : {};

  // Fetch controls when storyboard changes
  useEffect(() => {
    const fetchControls = async () => {
      if (!storyboard?.fullFilePath || !storyUniqueIdentifier) {
        // Clear controls and values for this storyCodebaseId if it exists
        if (storyUniqueIdentifier) {
          setControlsRecord((prev) => ({
            ...prev,
            [storyUniqueIdentifier]: {},
          }));
          setValuesRecord((prev) => ({ ...prev, [storyUniqueIdentifier]: {} }));
        }
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const supabaseJWT = await getUnexpiredToken(session);
        const response = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/parse/parseStoryControls`,
          { filepath: storyboard.fullFilePath },
          { headers: { Authorization: `Bearer ${supabaseJWT}` } },
        );

        const controlsFromServer: StoryControlsSpec = response.data || {};

        // Add placeholders to controls
        const controlsWithPlaceholders: StoryControlsSpec = {};
        for (const [key, control] of Object.entries(controlsFromServer)) {
          controlsWithPlaceholders[key] = {
            ...control,
            placeholder: generateStoryControlPlaceholder(control),
          };
        }

        // Store in records keyed by storyCodebaseId
        setControlsRecord((prev) => ({
          ...prev,
          [storyUniqueIdentifier]: controlsFromServer,
        }));

        if (controlsFromServer) {
          setCachedControls(storyUniqueIdentifier, controlsFromServer);
        }

        const initialValues = storyArgValues[storyUniqueIdentifier] || {};
        setValuesRecord((prev) => ({
          ...prev,
          [storyUniqueIdentifier]: initialValues,
        }));
      } catch (e) {
        console.error("Failed to fetch story controls:", e);
        setError("Failed to load story controls");
        // Clear for this storyCodebaseId on error
        if (storyUniqueIdentifier) {
          setControlsRecord((prev) => ({
            ...prev,
            [storyUniqueIdentifier]: {},
          }));
          setValuesRecord((prev) => ({ ...prev, [storyUniqueIdentifier]: {} }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchControls();
  }, [
    storyboard?.fullFilePath,
    canvasId,
    session,
    storyArgValues,
    storyUniqueIdentifier,
  ]);

  // Handler to update a single control value
  const handleChange = useCallback(
    async (changes: Record<string, StoryControlValue>) => {
      const propName = Object.keys(changes)[0];
      const value = changes[propName];

      if (!propName || !storyboard || !storyUniqueIdentifier) return;

      // Exit early if value is unchanged
      const currentValue = values[propName];
      if (currentValue === value) {
        return;
      }

      // Optimistic UI update for this specific storyCodebaseId
      setValuesRecord((prev) => ({
        ...prev,
        [storyUniqueIdentifier]: {
          ...prev[storyUniqueIdentifier],
          [propName]: value,
        },
      }));

      try {
        const supabaseJWT = await getUnexpiredToken(session);
        const controlType =
          controlsRecord[storyUniqueIdentifier]?.[propName]?.type || "string";

        const update = {
          [propName]: { type: controlType, value },
        };

        track("STORY_CONTROLS_EDIT", {
          controlType,
        });

        await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/mutate/updateStoryboardControls`,
          {
            storyName: storyboard.name,
            storyFilePath: storyboard.fullFilePath,
            storyboardId: storyboard.id,
            update,
            dynamicRouteFilePath: storyboard.fullFilePath,
          },
          { headers: { Authorization: `Bearer ${supabaseJWT}` } },
        );
      } catch (e) {
        console.error("Failed to update storyboard controls:", e);
        toast.error("Failed to update storyboard controls");
        // Revert the optimistic update using the value before this change
        // This avoids race conditions with stale closure values
        setValuesRecord((prev) => ({
          ...prev,
          [storyUniqueIdentifier]: {
            ...prev[storyUniqueIdentifier],
            [propName]: currentValue,
          },
        }));
      }
    },
    [
      controlsRecord,
      canvasId,
      storyboard,
      session,
      valuesRecord,
      storyUniqueIdentifier,
      values,
    ],
  );

  return {
    controls,
    values,
    loading,
    error,
    displayName: storyboard?.name || "Untitled Story",
    handleChange,
  } as const;
};

export const StoryControlsManager: React.FC = () => {
  const { controls, values, loading, error, displayName, handleChange } =
    useStoryboardControls();
  const hasControls = controls && Object.keys(controls).length > 0;

  return (
    <div className="w-full px-4 pt-4 py-2 space-y-2 controls-manager">
      <div className="flex items-center gap-1 py-1.5">
        <span className="p-[3px] flex items-center justify-center">
          <img
            src="/icons/storybook.svg"
            alt="storybook icon"
            className="w-3 h-3"
          />
        </span>
        <span className="text-xs font-medium text-white">{displayName}</span>
      </div>
      {error ? (
        <div className="text-[10px] text-red-400">{error}</div>
      ) : loading && !hasControls ? (
        <div className="text-[10px] text-[#9F9FA4]">
          Loading story controls...
        </div>
      ) : !hasControls ? (
        <div className="text-[10px] text-[#9F9FA4]">
          No story controls found for this storyboard
        </div>
      ) : (
        <ControlEditor
          controls={controls}
          values={values}
          onChange={handleChange}
        />
      )}
    </div>
  );
};

export default StoryControlsManager;
